import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from "../api/axiosInstance";
import AnimatedCounter from "../components/AnimatedCounter";
import ERROR_MESSAGES from "../constants/messages";

const FamiliesByCityPage = () => {
  const [cityStats, setCityStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [families, setFamilies] = useState(null);
  const [familiesLoading, setFamiliesLoading] = useState(false);
  const [familiesError, setFamiliesError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const navigate = useNavigate();
  const familiesRef = useRef(null);

  useEffect(() => {
    fetchCityStats();
  }, []);

  const fetchCityStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.post("/family/getFamilyCountByCity", {});
      setCityStats(response.data.cityFamilyCounts || []);
    } catch (err) {
      console.error("Error fetching city stats:", err);
      setError(err.response?.data?.operationMessage || ERROR_MESSAGES.DEFAULT);
    } finally {
      setLoading(false);
    }
  };

  const handleCityClick = async (city) => {
    setSelectedCity(city);
    setFamilies(null);
    setFamiliesError(null);
    setFamiliesLoading(true);
    try {
      const res = await api.post("/family/getFamiliesByRegion", {
        city: city.cityName,
        state: city.stateName,
        country: city.country || null,
      });
      setFamilies(res.data.families || []);
    } catch (err) {
      console.error("Error fetching families by region:", err);
      setFamiliesError(err.response?.data?.operationMessage || ERROR_MESSAGES.DEFAULT);
    } finally {
      setFamiliesLoading(false);
      setTimeout(() => familiesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    }
  };

  const getTotalFamilies = () => cityStats.reduce((sum, city) => sum + city.familyCount, 0);
  const getTotalMembers = () => cityStats.reduce((sum, city) => sum + (city.memberCount || 0), 0);
  const getTotalCities = () => cityStats.length;

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

  const groupedByState = cityStats.reduce((acc, city) => {
    const key = `${city.stateName || ""}||${city.country || ""}`;
    if (!acc[key]) {
      acc[key] = { stateKey: key, stateName: city.stateName || "", country: city.country || "", totalFamilies: 0, totalMembers: 0, cities: [] };
    }
    acc[key].cities.push(city);
    acc[key].totalFamilies += city.familyCount;
    acc[key].totalMembers += city.memberCount ?? 0;
    return acc;
  }, {});

  const compareVals = (aVal, bVal) => {
    if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  };

  const sortCities = (cities) => [...cities].sort((a, b) => {
    if (!sortConfig.key || sortConfig.key === "state") return (a.cityName || "").toLowerCase().localeCompare((b.cityName || "").toLowerCase());
    if (sortConfig.key === "city") return compareVals((a.cityName || "").toLowerCase(), (b.cityName || "").toLowerCase());
    if (sortConfig.key === "families") return compareVals(a.familyCount, b.familyCount);
    return compareVals(a.memberCount ?? 0, b.memberCount ?? 0);
  });

  const sortedGroups = Object.values(groupedByState).sort((a, b) => {
    const stateA = `${a.stateName} ${a.country}`.toLowerCase();
    const stateB = `${b.stateName} ${b.country}`.toLowerCase();
    if (!sortConfig.key || sortConfig.key === "city") return stateA < stateB ? -1 : stateA > stateB ? 1 : 0;
    if (sortConfig.key === "state") return compareVals(stateA, stateB);
    if (sortConfig.key === "families") return compareVals(a.totalFamilies, b.totalFamilies);
    return compareVals(a.totalMembers, b.totalMembers);
  });

  const SortIcon = ({ colKey }) => {
    if (sortConfig.key !== colKey) return <span style={{ color: "#aaa", marginLeft: 4 }}>⇅</span>;
    return <span style={{ marginLeft: 4 }}>{sortConfig.direction === "asc" ? "↑" : "↓"}</span>;
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: "24px" }}>Loading city statistics...</div>;
  }

  if (error) {
    return <div style={{ textAlign: "center", padding: "24px", color: "#dc3545" }}>{error}</div>;
  }

  return (
    <div style={{ padding: "24px" }}>
      {/* Summary Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: "24px",
          marginBottom: "32px",
        }}
      >
        <div
          style={{
            padding: "20px",
            borderRadius: "16px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <div style={{ fontSize: 40, fontWeight: 800, color: "#A42502" }}>
            <AnimatedCounter target={getTotalCities()} duration={1500} startOnView={true} once={true} suffix="" decimals={0} />
          </div>
          <div style={{ color: "#000", fontWeight: 600, fontSize: "18px" }}>Total Cities</div>
        </div>
        <div
          style={{
            padding: "20px",
            borderRadius: "16px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <div style={{ fontSize: 40, fontWeight: 800, color: "#A42502" }}>
            <AnimatedCounter target={getTotalFamilies()} duration={1500} startOnView={true} once={true} suffix="" decimals={0} />
          </div>
          <div style={{ color: "#000", fontWeight: 600, fontSize: "18px" }}>Total Families</div>
        </div>
        <div
          style={{
            padding: "20px",
            borderRadius: "16px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <div style={{ fontSize: 40, fontWeight: 800, color: "#A42502" }}>
            <AnimatedCounter target={getTotalMembers()} duration={1500} startOnView={true} once={true} suffix="" decimals={0} />
          </div>
          <div style={{ color: "#000", fontWeight: 600, fontSize: "18px" }}>Total Members</div>
        </div>
      </div>

      {/* City Stats Table */}
      {cityStats.length === 0 ? (
        <div style={{ padding: "24px", color: "#6c757d" }}>No city data available yet.</div>
      ) : (
        <table className="table table-bordered table-hover">
          <thead className="table-light">
            <tr>
              <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("city")}>
                City <SortIcon colKey="city" />
              </th>
              <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("families")}>
                Families <SortIcon colKey="families" />
              </th>
              <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("members")}>
                Members <SortIcon colKey="members" />
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedGroups.map((group) => (
              <React.Fragment key={group.stateKey}>
                <tr style={{ background: "#A42502", userSelect: "none" }}>
                  <td style={{ fontWeight: 700, color: "#A42502" }}>
                    {group.stateName || "Unknown State"}
                    {group.country && (
                      <span style={{ fontSize: "13px", fontWeight: 400, marginLeft: "8px" }}>
                        {group.country}
                      </span>
                    )}
                  </td>
                  <td style={{ fontWeight: 600 }}>{group.totalFamilies}</td>
                  <td style={{ fontWeight: 600 }}>{group.totalMembers}</td>
                </tr>
                {sortCities(group.cities).map((city, index) => (
                  <tr
                    key={city.cityName + index}
                    style={{ cursor: "pointer" }}
                    className={
                      selectedCity?.cityName === city.cityName &&
                      selectedCity?.stateName === city.stateName &&
                      selectedCity?.country === city.country
                        ? "table-active"
                        : ""
                    }
                    onClick={() => handleCityClick(city)}
                  >
                    <td style={{ paddingLeft: "24px" }}>{city.cityName}</td>
                    <td>{city.familyCount}</td>
                    <td>{city.memberCount ?? 0}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}

      {/* Families for selected city */}
      {selectedCity && (
        <div className="mt-4" ref={familiesRef}>
          <h5>
            Families in {selectedCity.cityName}
            {selectedCity.stateName ? `, ${selectedCity.stateName}` : ""}
            {selectedCity.country ? `, ${selectedCity.country}` : ""}
          </h5>

          {familiesLoading && <div>Loading families...</div>}
          {familiesError && <div className="alert alert-danger">{familiesError}</div>}
          {families && families.length === 0 && !familiesLoading && (
            <div className="alert alert-info">No families found for this city.</div>
          )}

          {families && families.length > 0 && (
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>Head Of Family</th>
                  <th>Family Name</th>
                  <th>Gotra</th>
                </tr>
              </thead>
              <tbody>
                {families.map((family) => (
                  <tr
                    key={family.familyId}
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate(`/family/${family.familyId}`)}
                  >
                    <td>
                      {family.headOfFamilyFirstName}
                      {family.headOfFamilyFirstNameInHindi &&
                        ` (${family.headOfFamilyFirstNameInHindi})`}
                    </td>
                    <td>
                      {family.familyName}
                      {family.familyNameInHindi && ` (${family.familyNameInHindi})`}
                    </td>
                    <td>{family.gotra}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default FamiliesByCityPage;
